import React, { useEffect, useRef } from "react";
import { useAtom, useSetAtom } from "jotai";
import Cookies from "js-cookie";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { DEFAULT_TEXT_MODEL, DEFAULT_IMAGE_MODEL, imageModels } from "@/lib/aiproviders/models";
import { COOKIE_NAMES, COOKIE_EXPIRY_DAYS } from "@/lib/constants/cookies";
import { useChatFocus } from "@/contexts/ChatFocusContext";
import { useSubmitMessage } from "@/hooks/useSubmitMessage";
import { useAIChat } from "@/hooks/useAIChat";
import { getChat } from "instantdb/client/chat";
import {
  chatInputsAtom,
  clearChatInputAtom,
  currentInputAtom,
  updateChatInputAtom,
} from "@/state/chatAtoms";
import {
  AITextarea,
  type AITextareaRef,
  type AITextareaFormData,
} from "@/components/ui/ai-textarea";
import { AIMessages, type AIMessagesRef } from "@/components/chat/AIMessages";
import { WelcomeScreen } from "@/components/ui/welcome-screen";
import { useUser } from "@/hooks/useUser";
import { LegalLinks } from "@/components/ui/legal-links";
import { APP_NAME } from "@/config/constants";

interface CopilotChatProps {
  textModel?: string;
  imageModel?: string;
  imageMode?: boolean;
  firstMessage?: { chatId: string; content: string; model: string } | null;
  onFirstMessageSent?: () => void;
}

export function CopilotChat({
  textModel = DEFAULT_TEXT_MODEL,
  imageModel = DEFAULT_IMAGE_MODEL,
  imageMode: initialImageMode = false,
  firstMessage,
  onFirstMessageSent,
}: CopilotChatProps) {
  const params = useParams({ strict: false });
  const chatId = params.chatId;
  const navigate = useNavigate();
  const { registerFocusHandler, setIsChatInputFocused, setupAutoFocus, focusChatInput } =
    useChatFocus();
  const updateChatInput = useSetAtom(updateChatInputAtom);
  const clearChatInput = useSetAtom(clearChatInputAtom);
  const { isAuthenticated, userId } = useUser();
  const [currentInput, setCurrentInput] = useAtom(currentInputAtom);
  const [chatInputs] = useAtom(chatInputsAtom);
  const [showReservedSpace, setShowReservedSpace] = React.useState(false);
  const [imageMode, setImageMode] = React.useState(initialImageMode);

  // Form setup with react-hook-form
  const form = useForm<AITextareaFormData>({
    defaultValues: {
      message: currentInput || "", // Initialize with currentInput for new chats
      model: initialImageMode ? imageModel : textModel,
      imageMode: initialImageMode,
    },
  });

  // Update imageMode when initialImageMode prop changes
  React.useEffect(() => {
    setImageMode(initialImageMode);
    form.setValue("imageMode", initialImageMode);
  }, [initialImageMode, form]);

  // Update form model when textModel or imageModel props change
  React.useEffect(() => {
    const newModel = imageMode ? imageModel : textModel;
    if (newModel && form.getValues("model") !== newModel) {
      form.setValue("model", newModel);
    }
  }, [textModel, imageModel, imageMode, form]);

  // Watch model changes for UI updates only
  const selectedModel = form.watch("model");

  const aiChatRef = useRef<AITextareaRef>(null);
  const hasProcessedFirstMessage = useRef(false);

  // Use hooks for message submission and AI chat
  const { submitMessage } = useSubmitMessage({ chatId });
  const { messages, status, isPending, sendToAI, stop } = useAIChat({
    chatId,
    model: selectedModel || textModel,
  });

  // Get chat data for UI state
  const { chat: activeChat, error: chatError, isLoading: isChatLoading } = getChat(chatId);

  const isOwner = activeChat?.userId === userId;

  useEffect(() => {
    // Find the chat to get its title
    if (activeChat) {
      document.title = activeChat.title + " - " + APP_NAME;
    }
  }, [activeChat]);

  const aiMessagesRef = useRef<AIMessagesRef>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const hasSetInitialModel = useRef(false);

  // Helper function to update model cookies
  const updateModelCookies = React.useCallback((model: string, isImageMode: boolean) => {
    Cookies.set(COOKIE_NAMES.IMAGE_MODE, String(isImageMode), {
      expires: COOKIE_EXPIRY_DAYS,
    });

    if (isImageMode) {
      Cookies.set(COOKIE_NAMES.SELECTED_IMAGE_MODEL, model, {
        expires: COOKIE_EXPIRY_DAYS,
      });
    } else {
      Cookies.set(COOKIE_NAMES.SELECTED_TEXT_MODEL, model, {
        expires: COOKIE_EXPIRY_DAYS,
      });
    }
  }, []);

  // Handle first message after navigation
  if (firstMessage && !hasProcessedFirstMessage.current && chatId) {
    hasProcessedFirstMessage.current = true;
    // Trigger the AI immediately
    sendToAI(firstMessage.content, firstMessage.model).then(() => {
      onFirstMessageSent?.();
    });
  }

  // Sync form message with stored input values when chat changes
  React.useEffect(() => {
    const storedValue = activeChat ? chatInputs[activeChat.id] || "" : currentInput;
    const currentFormValue = form.getValues("message");

    // Only update if the stored value is different from current form value
    // This prevents infinite loops and preserves user typing
    if (storedValue !== currentFormValue) {
      form.setValue("message", storedValue);
    }
  }, [activeChat?.id, chatInputs, currentInput, form]);

  // Set model from latest message when opening a chat (only once per chat)
  React.useEffect(() => {
    if (!activeChat || messages.length === 0 || hasSetInitialModel.current) {
      return;
    }

    // Find the latest message with a model (prefer assistant messages, then user messages)
    const latestMessageWithModel = messages
      .slice()
      .reverse()
      .find((msg) => msg.metadata && typeof msg.metadata === "object" && "model" in msg.metadata);

    if (
      latestMessageWithModel?.metadata &&
      typeof latestMessageWithModel.metadata === "object" &&
      "model" in latestMessageWithModel.metadata
    ) {
      const messageModel = (latestMessageWithModel.metadata as any).model;
      if (messageModel) {
        form.setValue("model", messageModel);

        // Determine if this is an image model and update state accordingly
        const isImageModel = imageModels.some((m) => m.id === messageModel);
        setImageMode(isImageModel);
        form.setValue("imageMode", isImageModel);

        // Update cookies to preserve this model selection for new chats
        updateModelCookies(messageModel, isImageModel);

        hasSetInitialModel.current = true;
      }
    }
  }, [activeChat, messages, form, updateModelCookies]);

  // Reset the hasSetInitialModel flag when switching to a different chat
  React.useEffect(() => {
    hasSetInitialModel.current = false;
  }, [activeChat?.id]);

  // Set initial value from currentInput for new chats on mount
  React.useEffect(() => {
    if (!chatId && currentInput) {
      form.setValue("message", currentInput);
    }
  }, [currentInput]); // Only run once on mount

  // Register focus handler with context
  React.useEffect(() => {
    const focusInput = () => {
      if (aiChatRef.current) {
        aiChatRef.current.focus();
      }
    };
    registerFocusHandler(focusInput);
  }, [registerFocusHandler]);

  // Auto-focus when active chat changes or on mount
  React.useEffect(() => {
    setupAutoFocus(!!activeChat?.id || !chatId);
  }, [activeChat?.id, chatId, setupAutoFocus]);

  const handleAIChatSubmit = React.useCallback(
    async (data: AITextareaFormData) => {
      // Scroll to bottom immediately when submitting
      requestAnimationFrame(() => {
        aiMessagesRef.current?.scrollToBottom(true);
      });

      // Submit message through the hook
      const wasSubmitted = await submitMessage(data);

      setShowReservedSpace(true);

      // Only clear input and trigger AI if message was successfully submitted
      if (wasSubmitted) {
        // Clear form
        form.reset({ message: "", model: data.model });

        // Clear stored input for this specific chat or current input
        if (activeChat) {
          clearChatInput(activeChat.id);
        } else {
          setCurrentInput("");
        }

        // Only trigger AI for existing chats (new chats will handle AI after navigation)
        if (chatId) {
          await sendToAI(data.message, data.model);
        }
      }
    },
    [
      submitMessage,
      form,
      activeChat,
      clearChatInput,
      setCurrentInput,
      chatId,
      sendToAI,
      updateModelCookies,
      imageMode,
    ],
  );

  // Update model in form and cookie
  const handleModelChange = React.useCallback(
    (newModel: string) => {
      form.setValue("model", newModel);
      updateModelCookies(newModel, imageMode);
    },
    [form, imageMode, updateModelCookies],
  );

  // Handle image mode toggle
  const handleImageModeChange = React.useCallback(
    (newImageMode: boolean) => {
      setImageMode(newImageMode);
      form.setValue("imageMode", newImageMode);

      // Switch to the appropriate model
      const newModel = newImageMode ? imageModel : textModel;
      if (newModel) {
        form.setValue("model", newModel);
        updateModelCookies(newModel, newImageMode);
      }
    },
    [form, imageModel, textModel, updateModelCookies],
  );

  const handleSuggestionClick = React.useCallback(
    (suggestion: string) => {
      const newValue = `${suggestion} `;

      // Set value in the form (controlled input will update automatically)
      form.setValue("message", newValue);

      // Store in atom for persistence
      if (activeChat) {
        updateChatInput(activeChat.id, newValue);
      } else {
        setCurrentInput(newValue);
      }

      // Focus immediately
      requestAnimationFrame(() => {
        focusChatInput();
      });
    },
    [form, activeChat, updateChatInput, setCurrentInput, focusChatInput],
  );

  // Handle error state only when we're expecting a chat
  if (chatId && chatError) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <div className="text-center">
          <p className="text-muted-foreground">Failed to load chat</p>
          <p className="text-xs text-muted-foreground mt-1">{chatError.message}</p>
        </div>
      </div>
    );
  }

  // If we have a chatId but no active chat found, redirect to home
  if (chatId && !activeChat && !isChatLoading) {
    navigate({ to: "/" });
    return null;
  }

  const showWelcomeScreen = messages.length === 0 && !chatId;

  return (
    <div className="relative h-screen select-none">
      <div className="h-full overflow-auto" ref={scrollContainerRef}>
        {showWelcomeScreen ? (
          <div className="pt-[57px] pb-[160px]">
            <div className="flex flex-col items-center h-full select-none pt-[20vh]">
              <div className="max-w-4xl mx-auto">
                <WelcomeScreen onSuggestionClick={handleSuggestionClick} />
              </div>
            </div>
          </div>
        ) : (
          <AIMessages
            ref={aiMessagesRef}
            messages={messages}
            currentModel={selectedModel}
            isPending={isPending || status === "streaming" || status === "submitted"}
            chatId={chatId}
            scrollContainer={scrollContainerRef.current}
            onRetry={(content, model) => {
              sendToAI(content, model);
            }}
            imageMode={imageMode}
            onModelChange={handleModelChange}
            showReservedSpace={showReservedSpace}
            isOwner={isOwner}
          />
        )}
      </div>

      {(isOwner || !chatId) && (
        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-4xl mx-auto px-4 pb-4">
            <div className="backdrop-blur-lg bg-background/20 rounded-lg p-4">
              <AITextarea
                ref={aiChatRef}
                form={form}
                onSubmit={handleAIChatSubmit}
                placeholder="Tell me what you need help with..."
                isLoading={status === "streaming" || isPending}
                onStop={stop}
                textModel={textModel}
                imageModel={imageModel}
                imageMode={imageMode}
                onImageModeChange={handleImageModeChange}
                onValueChange={(value) => {
                  // Persist to Jotai atoms as user types
                  if (activeChat) {
                    updateChatInput(activeChat.id, value);
                  } else {
                    setCurrentInput(value);
                  }
                }}
                onFocus={() => {
                  setIsChatInputFocused(true);
                }}
                onBlur={() => {
                  setIsChatInputFocused(false);
                }}
              />
            </div>
            {!isAuthenticated && <LegalLinks prefix="Make sure you agree to our" />}
          </div>
        </div>
      )}
    </div>
  );
}
